import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Shield,
  Smartphone,
  Mail,
  Key,
  QrCode,
  Copy,
  Download,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  Trash2,
  Plus,
  Settings
} from 'lucide-react';

interface TwoFactorAuthProps {
  onSetupComplete?: (method: string) => void;
  onDisable?: (method: string) => void;
  user?: {
    id: string;
    email: string;
    phone?: string;
  };
}

interface BackupCode {
  id: string;
  code: string;
  used: boolean;
  createdAt: string;
}

interface TwoFactorMethod {
  id: string;
  type: 'totp' | 'sms' | 'email';
  enabled: boolean;
  verified: boolean;
  createdAt: string;
  lastUsed?: string;
}

const TwoFactorAuth: React.FC<TwoFactorAuthProps> = ({
  onSetupComplete,
  onDisable,
  user = {
    id: 'user-1',
    email: 'admin@neobridge.com',
    phone: '+1234567890'
  }
}) => {
  const [methods, setMethods] = useState<TwoFactorMethod[]>([
    {
      id: 'totp-1',
      type: 'totp',
      enabled: false,
      verified: false,
      createdAt: new Date().toISOString()
    },
    {
      id: 'sms-1',
      type: 'sms',
      enabled: false,
      verified: false,
      createdAt: new Date().toISOString()
    },
    {
      id: 'email-1',
      type: 'email',
      enabled: false,
      verified: false,
      createdAt: new Date().toISOString()
    }
  ]);

  const [backupCodes, setBackupCodes] = useState<BackupCode[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [qrCode, setQrCode] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [smsCode, setSmsCode] = useState<string>('');
  const [emailCode, setEmailCode] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Generate backup codes
  const generateBackupCodes = () => {
    const codes: BackupCode[] = [];
    for (let i = 0; i < 10; i++) {
      codes.push({
        id: `backup-${i + 1}`,
        code: Math.random().toString(36).substring(2, 10).toUpperCase(),
        used: false,
        createdAt: new Date().toISOString()
      });
    }
    setBackupCodes(codes);
    setShowBackupCodes(true);
  };

  // Setup TOTP
  const setupTOTP = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Generate secret key (in real app, this would come from backend)
      const secret = 'JBSWY3DPEHPK3PXP';
      setSecretKey(secret);
      
      // Generate QR code URL
      const qrUrl = `otpauth://totp/NeoBridge:${user.email}?secret=${secret}&issuer=NeoBridge`;
      setQrCode(qrUrl);
      
      setSuccess('TOTP setup initiated. Scan the QR code with your authenticator app.');
    } catch (err) {
      setError('Failed to setup TOTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify TOTP code
  const verifyTOTP = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Simulate verification (in real app, this would verify with backend)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (verificationCode === '123456') { // Demo code
        const updatedMethods = methods.map(method => 
          method.type === 'totp' 
            ? { ...method, enabled: true, verified: true, lastUsed: new Date().toISOString() }
            : method
        );
        setMethods(updatedMethods);
        setSuccess('TOTP authentication enabled successfully!');
        onSetupComplete?.('totp');
        generateBackupCodes();
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Setup SMS
  const setupSMS = async () => {
    setLoading(true);
    setError('');

    try {
      // Simulate SMS setup (in real app, this would send SMS via backend)
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess('SMS code sent to your phone. Please enter the 6-digit code.');
    } catch (err) {
      setError('Failed to send SMS code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify SMS code
  const verifySMS = async () => {
    if (!smsCode || smsCode.length !== 6) {
      setError('Please enter a valid 6-digit SMS code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (smsCode === '654321') { // Demo code
        const updatedMethods = methods.map(method => 
          method.type === 'sms' 
            ? { ...method, enabled: true, verified: true, lastUsed: new Date().toISOString() }
            : method
        );
        setMethods(updatedMethods);
        setSuccess('SMS authentication enabled successfully!');
        onSetupComplete?.('sms');
      } else {
        setError('Invalid SMS code. Please try again.');
      }
    } catch (err) {
      setError('SMS verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Setup Email
  const setupEmail = async () => {
    setLoading(true);
    setError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess('Email code sent to your email address. Please enter the 6-digit code.');
    } catch (err) {
      setError('Failed to send email code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify Email code
  const verifyEmail = async () => {
    if (!emailCode || emailCode.length !== 6) {
      setError('Please enter a valid 6-digit email code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (emailCode === '789012') { // Demo code
        const updatedMethods = methods.map(method => 
          method.type === 'email' 
            ? { ...method, enabled: true, verified: true, lastUsed: new Date().toISOString() }
            : method
        );
        setMethods(updatedMethods);
        setSuccess('Email authentication enabled successfully!');
        onSetupComplete?.('email');
      } else {
        setError('Invalid email code. Please try again.');
      }
    } catch (err) {
      setError('Email verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Disable method
  const disableMethod = async (methodId: string) => {
    setLoading(true);
    setError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedMethods = methods.map(method => 
        method.id === methodId 
          ? { ...method, enabled: false, verified: false }
          : method
      );
      setMethods(updatedMethods);
      setSuccess('Two-factor authentication method disabled successfully.');
      onDisable?.(methods.find(m => m.id === methodId)?.type || '');
    } catch (err) {
      setError('Failed to disable method. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Download backup codes
  const downloadBackupCodes = () => {
    const content = backupCodes.map(code => `${code.code} - ${code.used ? 'USED' : 'AVAILABLE'}`).join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'neobridge-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'totp': return <Smartphone className="h-5 w-5" />;
      case 'sms': return <Mail className="h-5 w-5" />;
      case 'email': return <Mail className="h-5 w-5" />;
      default: return <Shield className="h-5 w-5" />;
    }
  };

  const getMethodName = (type: string) => {
    switch (type) {
      case 'totp': return 'Authenticator App';
      case 'sms': return 'SMS';
      case 'email': return 'Email';
      default: return 'Unknown';
    }
  };

  const enabledMethods = methods.filter(method => method.enabled && method.verified);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Two-Factor Authentication</h2>
          <p className="text-muted-foreground">
            Add an extra layer of security to your account
          </p>
        </div>
        <Badge variant={enabledMethods.length > 0 ? 'default' : 'secondary'}>
          {enabledMethods.length > 0 ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      {/* Status Alert */}
      {enabledMethods.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Two-factor authentication is not enabled. We recommend enabling at least one method for enhanced security.
          </AlertDescription>
        </Alert>
      )}

      {/* Success/Error Messages */}
      {success && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Authentication Methods */}
      <Card>
        <CardHeader>
          <CardTitle>Authentication Methods</CardTitle>
          <CardDescription>
            Choose your preferred two-factor authentication method
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="totp" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="totp">Authenticator App</TabsTrigger>
              <TabsTrigger value="sms">SMS</TabsTrigger>
              <TabsTrigger value="email">Email</TabsTrigger>
            </TabsList>

            {/* TOTP Tab */}
            <TabsContent value="totp" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Smartphone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <h3 className="font-medium">Authenticator App</h3>
                    <p className="text-sm text-muted-foreground">
                      Use apps like Google Authenticator or Authy
                    </p>
                  </div>
                </div>
                <Badge variant={methods.find(m => m.type === 'totp')?.enabled ? 'default' : 'secondary'}>
                  {methods.find(m => m.type === 'totp')?.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              {!methods.find(m => m.type === 'totp')?.enabled ? (
                <div className="space-y-4">
                  <Button onClick={setupTOTP} disabled={loading}>
                    {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Setup Authenticator App
                  </Button>

                  {qrCode && (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="inline-block p-4 bg-white rounded-lg border">
                          <QrCode className="h-32 w-32" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          Scan this QR code with your authenticator app
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="secret-key">Secret Key</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="secret-key"
                            value={secretKey}
                            readOnly
                            className="font-mono"
                          />
                          <Button variant="outline" size="icon" onClick={() => navigator.clipboard.writeText(secretKey)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="verification-code">Verification Code</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="verification-code"
                            placeholder="Enter 6-digit code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            maxLength={6}
                            className="font-mono text-center"
                          />
                          <Button onClick={verifyTOTP} disabled={loading || verificationCode.length !== 6}>
                            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Verify'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Last used: {methods.find(m => m.type === 'totp')?.lastUsed ? 
                      new Date(methods.find(m => m.type === 'totp')!.lastUsed!).toLocaleString() : 
                      'Never'}
                  </div>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => disableMethod('totp-1')}
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Disable
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* SMS Tab */}
            <TabsContent value="sms" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <h3 className="font-medium">SMS</h3>
                    <p className="text-sm text-muted-foreground">
                      Receive codes via SMS to {user.phone}
                    </p>
                  </div>
                </div>
                <Badge variant={methods.find(m => m.type === 'sms')?.enabled ? 'default' : 'secondary'}>
                  {methods.find(m => m.type === 'sms')?.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              {!methods.find(m => m.type === 'sms')?.enabled ? (
                <div className="space-y-4">
                  <Button onClick={setupSMS} disabled={loading}>
                    {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Setup SMS Authentication
                  </Button>

                  {success.includes('SMS code sent') && (
                    <div className="space-y-2">
                      <Label htmlFor="sms-code">SMS Code</Label>
                      <div className="flex space-x-2">
                        <Input
                          id="sms-code"
                          placeholder="Enter 6-digit SMS code"
                          value={smsCode}
                          onChange={(e) => setSmsCode(e.target.value)}
                          maxLength={6}
                          className="font-mono text-center"
                        />
                        <Button onClick={verifySMS} disabled={loading || smsCode.length !== 6}>
                          {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Verify'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Last used: {methods.find(m => m.type === 'sms')?.lastUsed ? 
                      new Date(methods.find(m => m.type === 'sms')!.lastUsed!).toLocaleString() : 
                      'Never'}
                  </div>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => disableMethod('sms-1')}
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Disable
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <h3 className="font-medium">Email</h3>
                    <p className="text-sm text-muted-foreground">
                      Receive codes via email to {user.email}
                    </p>
                  </div>
                </div>
                <Badge variant={methods.find(m => m.type === 'email')?.enabled ? 'default' : 'secondary'}>
                  {methods.find(m => m.type === 'email')?.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              {!methods.find(m => m.type === 'email')?.enabled ? (
                <div className="space-y-4">
                  <Button onClick={setupEmail} disabled={loading}>
                    {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Setup Email Authentication
                  </Button>

                  {success.includes('Email code sent') && (
                    <div className="space-y-2">
                      <Label htmlFor="email-code">Email Code</Label>
                      <div className="flex space-x-2">
                        <Input
                          id="email-code"
                          placeholder="Enter 6-digit email code"
                          value={emailCode}
                          onChange={(e) => setEmailCode(e.target.value)}
                          maxLength={6}
                          className="font-mono text-center"
                        />
                        <Button onClick={verifyEmail} disabled={loading || emailCode.length !== 6}>
                          {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Verify'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Last used: {methods.find(m => m.type === 'email')?.lastUsed ? 
                      new Date(methods.find(m => m.type === 'email')!.lastUsed!).toLocaleString() : 
                      'Never'}
                  </div>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => disableMethod('email-1')}
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Disable
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Backup Codes */}
      {backupCodes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Backup Codes</CardTitle>
            <CardDescription>
              Use these codes to access your account if you lose your device
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {backupCodes.filter(code => !code.used).length} codes remaining
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={() => setShowBackupCodes(!showBackupCodes)}>
                    {showBackupCodes ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    {showBackupCodes ? 'Hide' : 'Show'} Codes
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadBackupCodes}>
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              {showBackupCodes && (
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code) => (
                    <div
                      key={code.id}
                      className={`p-3 rounded-md border font-mono text-sm ${
                        code.used ? 'bg-muted text-muted-foreground' : 'bg-background'
                      }`}
                    >
                      {code.code}
                    </div>
                  ))}
                </div>
              )}

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Store these backup codes in a safe place. Each code can only be used once.
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TwoFactorAuth;
